import { useState, useCallback, useEffect } from 'react'
import { Search, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import type { LeadDetail, PaginationInfo } from '../types'
import { fetchLeads, getExportUrl } from '../api'

interface Props { initialLeads: LeadDetail[]; totalLeads: number }

function fmtDate(d: string | null) { if (!d) return '-'; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
function fmtDur(s: number | null) { if (!s) return '-'; return `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}` }
function fmtCur(v: number | null) { if (!v || v === 0) return '-'; return `$${v.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2})}` }

export function ColdProsLeadsTable({ initialLeads, totalLeads }: Props) {
  const [leads, setLeads] = useState<LeadDetail[]>(initialLeads)
  const [pagination, setPagination] = useState<PaginationInfo>({ page:1, per_page:25, total:totalLeads, total_pages:Math.ceil(totalLeads/25) })
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)

  const loadPage = useCallback(async (page: number, q?: string) => {
    setLoading(true)
    try { const r = await fetchLeads({ page, per_page:25, search:q }); setLeads(r.leads); setPagination(r.pagination) }
    catch {} finally { setLoading(false) }
  }, [])

  useEffect(() => { const t = setTimeout(() => loadPage(1, search || undefined), 300); return () => clearTimeout(t) }, [search, loadPage])

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between p-5 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">Call Details</h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search name, phone, job #..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-64" />
          </div>
          <a href={getExportUrl()} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </a>
        </div>
      </div>
      <div className={`overflow-x-auto ${loading ? 'opacity-50' : ''}`}>
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Caller</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Phone</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Duration</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">City</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Job #</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Job Status</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Tech</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">Payment</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {leads.map((l) => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(l.activity_date)}</td>
                <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">{l.caller_name || '-'}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{l.caller_number || '-'}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDur(l.call_duration)}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{l.city || '-'}</td>
                <td className="px-4 py-3 whitespace-nowrap">{l.job_number ? <span className="font-mono text-blue-600">#{l.job_number}</span> : <span className="text-gray-400">-</span>}</td>
                <td className="px-4 py-3 whitespace-nowrap">{l.job_status ? <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${l.job_status === 'Done' ? 'bg-green-100 text-green-700' : l.job_status.includes('Cancel') ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{l.job_status}</span> : <span className="text-gray-400">-</span>}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{l.tech || '-'}</td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium whitespace-nowrap">{fmtCur(l.payment_amount)}</td>
                <td className="px-4 py-3 whitespace-nowrap"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${l.payment_status === 'Paid' ? 'bg-green-100 text-green-700' : l.payment_status === 'Unpaid' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>{l.payment_status}</span></td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No calls found</td></tr>}
          </tbody>
        </table>
      </div>
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200">
          <span className="text-sm text-gray-500">Showing {(pagination.page-1)*pagination.per_page+1}-{Math.min(pagination.page*pagination.per_page, pagination.total)} of {pagination.total}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => loadPage(pagination.page-1, search||undefined)} disabled={pagination.page<=1} className="p-1.5 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.total_pages}</span>
            <button onClick={() => loadPage(pagination.page+1, search||undefined)} disabled={pagination.page>=pagination.total_pages} className="p-1.5 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  )
}
